<template>
    <div
        v-click-outside="() => onCancel()"
        class="relative inline-flex shrink-0"
        :data-spec-class="dataSpecClass"
    >
        <div
            data-spec-class="tooltip__trigger"
            class="scale-100 cursor-pointer transition hover:scale-105"
            @click="onUpdate"
            @mouseenter="onHoverStart"
            @mouseleave="onHoverEnd"
        >
            <slot name="trigger" />
        </div>
        <span
            v-if="isActive"
            data-spec-class="tooltip__message"
            class="absolute z-50 inline-block w-64 rounded-md border p-2 text-sm break-words whitespace-normal"
            :class="{
                '-m-1': size === Size.Normal,
                'top-0 right-0 mr-9': position === TooltipPosition.Left,
                'top-0 left-full ml-2': position === TooltipPosition.Right,
                'top-full left-1/2 mt-2 -translate-x-1/2': position === TooltipPosition.Bottom,
                'border-gray-400 bg-gray-600 text-gray-100 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100':
                    theme === Theme.Dark,
                'border-gray-200 bg-gray-100 text-gray-700 dark:border-gray-700 dark:bg-gray-700 dark:text-gray-100':
                    theme === Theme.Light,
            }"
            @mouseenter="onTooltipEnter"
            @mouseleave="onTooltipLeave"
        >
            <slot />
        </span>
    </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { Theme, TooltipPosition, Size } from '@/types';

interface Props {
    theme?: Theme;
    position?: TooltipPosition;
    size?: Size;
    dataSpecClass?: string;
}

withDefaults(defineProps<Props>(), {
    theme: Theme.Dark,
    position: TooltipPosition.Left,
    size: Size.Normal,
    dataSpecClass: 'tooltip',
});

const emits = defineEmits(['update']);

const isActive = ref(false);
const hoverTimeout = ref<number | null>(null);
const closeTimeout = ref<number | null>(null);

function onUpdate() {
    isActive.value = !isActive.value;
    emits('update', isActive.value);
}

function onCancel() {
    clearHoverTimeout();
    clearCloseTimeout();
    isActive.value = false;
    emits('update', isActive.value);
}

function onHoverStart() {
    clearCloseTimeout();
    hoverTimeout.value = window.setTimeout(() => {
        isActive.value = true;
        emits('update', isActive.value);
    }, 300);
}

function onHoverEnd() {
    clearHoverTimeout();
    clearCloseTimeout();
    closeTimeout.value = window.setTimeout(() => {
        isActive.value = false;
        emits('update', isActive.value);
    }, 100);
}

function onTooltipEnter() {
    clearCloseTimeout();
}

function onTooltipLeave() {
    onCancel();
}

function clearHoverTimeout() {
    if (hoverTimeout.value !== null) {
        clearTimeout(hoverTimeout.value);
        hoverTimeout.value = null;
    }
}

function clearCloseTimeout() {
    if (closeTimeout.value !== null) {
        clearTimeout(closeTimeout.value);
        closeTimeout.value = null;
    }
}
</script>
